import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatPrice } from "@/lib/api";

const CATS = [
  { value: "peeling", label: "Пилинг" }, { value: "steaming", label: "Парение" },
  { value: "massage", label: "Массаж" }, { value: "spa_program", label: "СПА программа" },
  { value: "package", label: "Пакет" }, { value: "accessories_sale", label: "Продажа" },
  { value: "accessories_rental", label: "Прокат" },
];
const EMPTY = { name_kz: "", name_ru: "", description_kz: "", description_ru: "", category: "massage", price: 0, duration: "", photo: "", is_active: true, package_items: [] };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");

  const load = () => api.get("/admin/services").then((r) => setServices(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/menu").then(r => setMenuItems(r.data)).catch(() => {});
  }, []);

  const filtered = filterCat === "all" ? services : services.filter((s) => s.category === filterCat);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { ...form, package_items: form.category === "package" ? (form.package_items || []) : [] };
      if (editId) await api.put(`/admin/services/${editId}`, payload);
      else await api.post("/admin/services", payload);
      setDialogOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch { alert("Ошибка"); }
    setLoading(false);
  };

  const handleEdit = (s) => { setForm({ ...EMPTY, ...s, package_items: s.package_items || [] }); setEditId(s.id); setDialogOpen(true); };
  const handleDelete = async (id) => { if (window.confirm("Удалить?")) { await api.delete(`/admin/services/${id}`); load(); } };

  // Package item editing helpers
  const addPkgItem = (type) => {
    setForm({ ...form, package_items: [...(form.package_items || []), { item_type: type, item_id: "", name_ru: "", name_kz: "", quantity: 1 }] });
  };
  const updatePkgItem = (idx, patch) => {
    const arr = [...(form.package_items || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setForm({ ...form, package_items: arr });
  };
  const removePkgItem = (idx) => {
    const arr = [...(form.package_items || [])];
    arr.splice(idx, 1);
    setForm({ ...form, package_items: arr });
  };
  const onPkgItemSelect = (idx, id) => {
    const item = form.package_items[idx];
    let ref = null;
    if (item.item_type === "menu") ref = menuItems.find(m => m.id === id);
    else ref = services.find(s => s.id === id);
    updatePkgItem(idx, { item_id: id, name_ru: ref?.name_ru || "", name_kz: ref?.name_kz || "" });
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Услуги</h1>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="add-service-btn"><Plus className="w-4 h-4" /> Добавить</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Редактировать" : "Новая услуга"}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Название (KZ)</label>
                  <Input value={form.name_kz} onChange={(e) => setForm({ ...form, name_kz: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="service-name-kz" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Название (RU)</label>
                  <Input value={form.name_ru} onChange={(e) => setForm({ ...form, name_ru: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="service-name-ru" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Описание (KZ)</label>
                  <Textarea value={form.description_kz} onChange={(e) => setForm({ ...form, description_kz: e.target.value })} className="bg-background border-white/10 rounded-none" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Описание (RU)</label>
                  <Textarea value={form.description_ru} onChange={(e) => setForm({ ...form, description_ru: e.target.value })} className="bg-background border-white/10 rounded-none" />
                </div>
              </div>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-background border-white/10 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  {CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="Цена (тг)" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" data-testid="service-price-input" />
                <Input placeholder="Длительность" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="bg-background border-white/10 rounded-none" />
              </div>

              {/* Package contents editor — only for category=package */}
              {form.category === "package" && (
                <div className="border border-primary/20 p-3 space-y-3" data-testid="package-items-editor">
                  <p className="text-xs text-primary uppercase tracking-widest">Состав пакета</p>

                  {/* Kitchen section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Кухня и бар</p>
                      <Button size="sm" variant="outline" onClick={() => addPkgItem("menu")} className="rounded-none text-xs h-7" data-testid="add-pkg-menu">+ Добавить</Button>
                    </div>
                    {(form.package_items || []).filter(p => p.item_type === "menu").length === 0 && (
                      <p className="text-xs text-muted-foreground italic">Нет блюд / напитков</p>
                    )}
                    {(form.package_items || []).map((pi, idx) => pi.item_type !== "menu" ? null : (
                      <div key={idx} className="flex items-center gap-2" data-testid={`pkg-item-${idx}`}>
                        <Select value={pi.item_id} onValueChange={(v) => onPkgItemSelect(idx, v)}>
                          <SelectTrigger className="bg-background border-white/10 rounded-none flex-1 text-xs h-8"><SelectValue placeholder="Выберите блюдо" /></SelectTrigger>
                          <SelectContent className="bg-card border-white/10 max-h-60">
                            {menuItems.map(it => <SelectItem key={it.id} value={it.id}>{it.name_ru || it.name_kz}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input type="number" min={1} value={pi.quantity} onChange={(e) => updatePkgItem(idx, { quantity: Math.max(1, +e.target.value) })} className="bg-background border-white/10 rounded-none w-14 text-xs h-8" />
                        <button onClick={() => removePkgItem(idx)} className="text-red-400 hover:text-red-300" data-testid={`remove-pkg-${idx}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-white/5"></div>

                  {/* Services section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Услуги</p>
                      <Button size="sm" variant="outline" onClick={() => addPkgItem("service")} className="rounded-none text-xs h-7" data-testid="add-pkg-service">+ Добавить</Button>
                    </div>
                    {(form.package_items || []).filter(p => p.item_type === "service").length === 0 && (
                      <p className="text-xs text-muted-foreground italic">Нет услуг</p>
                    )}
                    {(form.package_items || []).map((pi, idx) => pi.item_type !== "service" ? null : (
                      <div key={idx} className="flex items-center gap-2" data-testid={`pkg-item-${idx}`}>
                        <Select value={pi.item_id} onValueChange={(v) => onPkgItemSelect(idx, v)}>
                          <SelectTrigger className="bg-background border-white/10 rounded-none flex-1 text-xs h-8"><SelectValue placeholder="Выберите услугу" /></SelectTrigger>
                          <SelectContent className="bg-card border-white/10 max-h-60">
                            {services.filter(s => s.category !== "package" && s.id !== editId).map(it => (
                              <SelectItem key={it.id} value={it.id}>{it.name_ru || it.name_kz}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input type="number" min={1} value={pi.quantity} onChange={(e) => updatePkgItem(idx, { quantity: Math.max(1, +e.target.value) })} className="bg-background border-white/10 rounded-none w-14 text-xs h-8" />
                        <button onClick={() => removePkgItem(idx)} className="text-red-400 hover:text-red-300" data-testid={`remove-pkg-${idx}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button onClick={handleSave} disabled={loading} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="service-save-btn">
                {loading ? "..." : "Сохранить"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilterCat("all")} className={`px-3 py-1 text-xs ${filterCat === "all" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-white/10"}`}>Все</button>
        {CATS.map((c) => (
          <button key={c.value} onClick={() => setFilterCat(c.value)} className={`px-3 py-1 text-xs ${filterCat === c.value ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-white/10"}`}>{c.label}</button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.map((s) => (
          <div key={s.id} className="flex items-center justify-between bg-card border border-white/5 p-3" data-testid={`admin-service-${s.id}`}>
            <div className="flex-1">
              <span className="text-xs text-primary uppercase tracking-widest mr-2">{CATS.find(c => c.value === s.category)?.label}</span>
              <span className="text-sm">{s.name_ru || s.name || ""}</span>
              <span className="text-xs text-muted-foreground ml-1">/ {s.name_kz || ""}</span>
              {s.duration && <span className="text-xs text-muted-foreground ml-2">{s.duration}</span>}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-primary font-semibold">{formatPrice(s.price)}</span>
              <button onClick={() => handleEdit(s)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-service-${s.id}`}><Pencil className="w-3 h-3" /></button>
              <button onClick={() => handleDelete(s.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-service-${s.id}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
